import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { t } from 'src/i18n';

/**
 * Floating chat bubble button displayed in bottom-right corner.
 * Dispatches 'toggle-chat' event when clicked.
 * Shows unread count badge when there are unread messages.
 */
@customElement('d-chat-bubble')
export class DChatBubble extends LitElement {
  static readonly styles = css`
    :host {
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 400;
      display: block;
    }

    :host([is-app-platform]) {
      bottom: 80px;
    }

    @media only screen and (min-width: 845px) {
      :host {
        bottom: 30px;
        right: 30px;
      }
    }

    /*
     * When the crisis (emergency) button is visible it is fixed at
     * right:20px / bottom:20px and is 40px tall. Stack the chat bubble
     * directly above it so the two buttons don't overlap. The attribute
     * selectors are more specific than the rules above, so this also
     * overrides the desktop and app-platform offsets.
     */
    :host([emergency-visible]),
    :host([emergency-visible][is-app-platform]) {
      right: 20px;
      bottom: 70px;
    }

    button {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: hsl(196, 83%, 50%);
      border: 3px solid white;
      box-sizing: border-box;
      box-shadow: 0 0 10px hsla(0, 0%, 0%, 0.3);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      transition:
        transform 0.3s,
        box-shadow 0.3s;
    }

    button:hover {
      transform: scale(1.05);
      box-shadow: 0 0 14px hsla(0, 0%, 0%, 0.4);
    }

    button:active {
      transform: scale(0.95);
    }

    .icon {
      width: 22px;
      height: 22px;
      fill: white;
    }

    .badge {
      position: absolute;
      top: -4px;
      right: -4px;
      min-width: 18px;
      height: 18px;
      border-radius: 9px;
      background: hsl(0, 90%, 55%);
      color: white;
      font-size: 11px;
      font-weight: bold;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 6px;
      box-shadow: 0 2px 4px hsla(0, 0%, 0%, 0.3);
    }

    .badge:empty {
      display: none;
    }
  `;

  @property({ type: Number })
  unreadCount = 0;

  @property({ type: Boolean, attribute: 'is-app-platform', reflect: true })
  isAppPlatform = false;

  /**
   * True when the crisis (emergency) button is also showing. Moves the chat
   * bubble up so it stacks above the crisis button instead of overlapping it.
   */
  @property({ type: Boolean, attribute: 'emergency-visible', reflect: true })
  emergencyVisible = false;

  private onClick() {
    this.dispatchEvent(new CustomEvent('toggle-chat', { composed: true, bubbles: true }));
  }

  render() {
    return html`
      <button @click=${this.onClick} aria-label=${t('chat.toggleChatAriaLabel')}>
        <svg class="icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
        </svg>
        <span class="badge">${this.unreadCount > 0 ? this.unreadCount : ''}</span>
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-chat-bubble': DChatBubble;
  }
}
